import React, { useState, useRef, useEffect } from 'react';

/**
 * PwPopover — small reusable CLICK-triggered popover primitive (shared).
 *
 * Opens on trigger click; closes on outside-click or Escape. No hover dependency
 * (the app's existing tooltips are hover-only; this is the clickable variant).
 * Originated in the payroll wizard; promoted to src/components/ on first reuse
 * outside it (the Role Editor floor messaging, BL-058). Class names keep the
 * `pw-popover` prefix; styles live in src/styles/components/popover.css.
 *
 * @param {React.ReactNode} trigger   - content of the trigger button
 * @param {React.ReactNode} children  - popover panel content
 * @param {string} ariaLabel          - accessible label for the trigger + panel
 * @param {'start'|'end'} [align]     - panel horizontal alignment (default 'start')
 * @param {'fixed'} [strategy]        - 'fixed' positions the panel against the
 *        VIEWPORT (escapes overflow-clipping ancestors like scrollable tables —
 *        impl-review r2 #7); closes on scroll so the panel can't detach from
 *        its trigger. Default (absolute) is unchanged for existing callers.
 */
const PwPopover = ({ trigger, children, ariaLabel, align = 'start', strategy }) => {
  const [open, setOpen] = useState(false);
  const [fixedPos, setFixedPos] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    // Capture phase + stopPropagation: inside a <Modal>, whose own Esc listener
    // sits on document, one Esc must close only this panel, not the dialog too.
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  // Fixed mode: the panel is viewport-positioned, so any scroll would leave it
  // floating detached from its trigger — close instead (capture phase catches
  // scrolls inside nested scroll containers too).
  useEffect(() => {
    if (!open || strategy !== 'fixed') return undefined;
    const onScroll = () => setOpen(false);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open, strategy]);

  const toggle = () => {
    if (strategy === 'fixed' && !open && ref.current) {
      const r = ref.current.getBoundingClientRect();
      // clientWidth (not innerWidth) — position:fixed coordinates exclude a
      // classic scrollbar, which innerWidth includes (impl-review r3 #5).
      const viewportWidth = document.documentElement.clientWidth;
      // Clamp so the panel (max-width 320px, see popover.css) stays on-screen:
      // scrolling closes the popover, so an off-screen sliver is unreachable.
      const PANEL_MAX = 320;
      const MARGIN = 8;
      setFixedPos(
        align === 'end'
          ? { top: r.bottom + 8, right: Math.max(MARGIN, viewportWidth - r.right) }
          : { top: r.bottom + 8, left: Math.max(MARGIN, Math.min(r.left, viewportWidth - PANEL_MAX - MARGIN)) },
      );
    }
    setOpen((o) => !o);
  };

  const panelStyle =
    strategy === 'fixed' && fixedPos ? { position: 'fixed', ...fixedPos } : undefined;

  return (
    <span className="pw-popover" ref={ref}>
      <button
        type="button"
        className="pw-popover__trigger"
        aria-label={ariaLabel}
        aria-expanded={open}
        onClick={toggle}
      >
        {trigger}
      </button>
      {open && (
        <div
          className={`pw-popover__panel pw-popover__panel--${align}`}
          style={panelStyle}
          role="dialog"
          aria-label={ariaLabel}
        >
          {children}
        </div>
      )}
    </span>
  );
};

export default PwPopover;
